import { useEffect, useRef, useState } from "react";
import { Presence } from "@/components/Presence";
import { ChevronUp, ChevronDown, Pin, X, Mic, Volume2, Keyboard, LogOut } from "lucide-react";

/* Three ways the on-screen words could live on a phone. Not linked from anywhere — a room for
   looking at the same problem three times before choosing. */

const ACCENT = "#E07A5F";

const SAMPLE = [
  { kind: "idea", text: "Repair as refusal" },
  { kind: "quote", text: "We did not want the prototype to behave. We wanted it to argue back.", src: "Session 4 · Nairobi" },
  { kind: "idea", text: "Who was not in the room" },
  { kind: "step", text: "Name the thing you would not do again" },
  { kind: "quote", text: "Technology must carry our stories, not only our code.", src: "Closing circle" },
  { kind: "idea", text: "The timeline was the design" },
  { kind: "step", text: "Hand the question back before answering it" },
];

const Ordinal = ({ i }) => (
  <span className="mt-[7px] shrink-0 font-mono text-[9px] tracking-[0.18em]" style={{ color: ACCENT }}>
    {String(i + 1).padStart(2, "0")}
  </span>
);

const Body = ({ n, small }) => {
  if (n.kind === "quote")
    return (
      <span className="block">
        <span className={`block font-display font-light italic leading-[1.3] ${small ? "text-[15px]" : "text-[17px]"}`} style={{ color: "var(--text-main)" }}>
          “{n.text}”
        </span>
        <span className="mt-2 block font-mono text-[8.5px] uppercase tracking-[0.2em]" style={{ color: "var(--text-faint)" }}>{n.src}</span>
      </span>
    );
  return (
    <span className={`block font-display font-light leading-[1.26] ${small ? "text-[16px]" : "text-[19px]"}`} style={{ color: "var(--text-main)" }}>
      {n.text}
    </span>
  );
};

/* one note, in the smoked-glass vessel, pinnable */
const Note = ({ n, i, pinned, onPin, small, dim }) => (
  <button
    onClick={() => onPin(i)}
    className="relative block w-full shrink-0 text-left transition-[opacity,transform] duration-700"
    style={{ opacity: dim ? 0.5 : 1, transform: dim ? "scale(0.96)" : "none", transformOrigin: "left center" }}
  >
    <span
      className="relative block overflow-hidden rounded-[18px] px-4 py-3.5"
      style={{
        background: `linear-gradient(152deg, color-mix(in srgb, ${ACCENT} 9%, rgba(22,21,19,0.74)), rgba(12,12,11,0.7))`,
        border: `1px solid color-mix(in srgb, ${ACCENT} ${pinned ? 55 : 22}%, rgba(244,239,230,0.07))`,
        boxShadow: pinned
          ? `0 18px 40px -18px rgba(0,0,0,0.8), 0 0 34px -10px ${ACCENT}99`
          : "0 18px 40px -20px rgba(0,0,0,0.7)",
        backdropFilter: "blur(16px)",
      }}
    >
      <span className="flex gap-3">
        <Ordinal i={i} />
        <Body n={n} small={small} />
      </span>
      {pinned && (
        <span className="absolute right-3 top-3 flex h-5 w-5 items-center justify-center rounded-full" style={{ background: `${ACCENT}26` }}>
          <Pin size={10} style={{ color: ACCENT }} />
        </span>
      )}
    </span>
  </button>
);

const Chrome = ({ children, label }) => (
  <div className="shrink-0">
    <p className="mb-3 font-mono text-[10px] uppercase tracking-[0.24em] text-[#6E7582]">{label}</p>
    <div
      className="relative mx-auto flex h-[760px] w-[360px] flex-col overflow-hidden rounded-[2.1rem] border"
      style={{ borderColor: "rgba(244,239,230,0.12)", background: "#0C0C0B" }}
    >
      {children}
    </div>
  </div>
);

const TopBar = () => (
  <div className="flex shrink-0 items-center justify-between px-5 pt-5">
    <span className="font-mono text-[8.5px] uppercase tracking-[0.22em]" style={{ color: ACCENT }}>| British Council Kenya Lab</span>
    <X size={14} style={{ color: "var(--text-faint)" }} />
  </div>
);

const Controls = () => (
  <div className="mx-4 mb-4 mt-3 flex shrink-0 items-center gap-2 rounded-[20px] border p-2" style={{ borderColor: "rgba(244,239,230,0.08)", background: "rgba(244,239,230,0.02)" }}>
    {[[Mic, "live"], [Volume2, "volume"], [Keyboard, "type"], [LogOut, "leave"]].map(([I, l], i) => (
      <span key={l} className="flex flex-1 flex-col items-center gap-1 rounded-2xl py-2.5" style={i === 0 ? { background: `${ACCENT}1a` } : undefined}>
        <I size={14} style={{ color: i === 0 ? ACCENT : "var(--text-dim)" }} />
        <span className="font-mono text-[7.5px] uppercase tracking-[0.16em]" style={{ color: i === 0 ? ACCENT : "var(--text-faint)" }}>{l}</span>
      </span>
    ))}
  </div>
);

/* ─────────── A · The ledger: a docked panel you can pull open ─────────── */
const Ledger = ({ notes, pins, pin }) => {
  const [open, setOpen] = useState(false);
  const box = useRef(null);
  useEffect(() => { box.current?.scrollTo({ top: box.current.scrollHeight, behavior: "smooth" }); }, [notes.length, open]);

  return (
    <Chrome label="A · The ledger — docked, pull it open">
      <TopBar />
      <div className="flex min-h-0 flex-1 flex-col">
        <div className="flex items-center justify-center transition-all duration-700" style={{ height: open ? 96 : 230 }}>
          <Presence state="speaking" accent={ACCENT} height={open ? 92 : 220} />
        </div>
        <p className="mb-1 text-center font-mono text-[8px] uppercase tracking-[0.2em]" style={{ color: "var(--text-faint)" }}>speaking · tap to cut in</p>

        <div
          className="mx-3 mt-auto flex min-h-0 flex-col rounded-t-[22px] border-x border-t transition-all duration-700"
          style={{ borderColor: "rgba(244,239,230,0.09)", background: "rgba(18,17,16,0.72)", height: open ? "100%" : 300 }}
        >
          <button onClick={() => setOpen(!open)} className="flex shrink-0 items-center justify-between px-4 py-3">
            <span className="flex items-center gap-2">
              <span className="h-[3px] w-7 rounded-full" style={{ background: "rgba(244,239,230,0.25)" }} />
              <span className="font-mono text-[8.5px] uppercase tracking-[0.2em]" style={{ color: "var(--text-faint)" }}>on the table · {notes.length}</span>
            </span>
            {open ? <ChevronDown size={13} style={{ color: ACCENT }} /> : <ChevronUp size={13} style={{ color: ACCENT }} />}
          </button>
          <div ref={box} className="flex min-h-0 flex-col gap-2.5 overflow-y-auto px-3 pb-4" style={{ scrollbarWidth: "none" }}>
            {notes.map((n, i) => (
              <Note key={i} n={n} i={i} pinned={pins.has(i)} onPin={pin} small dim={!pins.has(i) && i < notes.length - 3} />
            ))}
          </div>
        </div>
      </div>
      <Controls />
    </Chrome>
  );
};

/* ─────────── B · The stack: one card at a time, swipe through ─────────── */
const Stack = ({ notes, pins, pin }) => {
  const [at, setAt] = useState(notes.length - 1);
  const rail = useRef(null);
  const onScroll = () => {
    if (!rail.current) return;
    setAt(Math.round(rail.current.scrollLeft / rail.current.clientWidth));
  };
  useEffect(() => { rail.current?.scrollTo({ left: rail.current.clientWidth * (notes.length - 1) }); }, [notes.length]);

  return (
    <Chrome label="B · The stack — swipe through them">
      <TopBar />
      <div className="flex min-h-0 flex-1 flex-col">
        <div className="flex h-[200px] items-center justify-center">
          <Presence state="speaking" accent={ACCENT} height={190} />
        </div>
        <div className="flex items-center justify-between px-5 pb-2">
          <span className="font-mono text-[8.5px] uppercase tracking-[0.2em]" style={{ color: "var(--text-faint)" }}>on the table</span>
          <span className="font-mono text-[8.5px] tracking-[0.2em]" style={{ color: ACCENT }}>{at + 1} / {notes.length}</span>
        </div>
        <div ref={rail} onScroll={onScroll} className="flex min-h-0 flex-1 snap-x snap-mandatory items-center overflow-x-auto" style={{ scrollbarWidth: "none" }}>
          {notes.map((n, i) => (
            <div key={i} className="flex w-full shrink-0 snap-center items-center px-4">
              <Note n={n} i={i} pinned={pins.has(i)} onPin={pin} />
            </div>
          ))}
        </div>
        <div className="flex shrink-0 items-center justify-center gap-1.5 pb-1">
          {notes.map((_, i) => (
            <span key={i} className="h-[3px] rounded-full transition-all duration-300" style={{ width: i === at ? 16 : 5, background: i === at ? ACCENT : "rgba(244,239,230,0.2)" }} />
          ))}
        </div>
      </div>
      <Controls />
    </Chrome>
  );
};

/* ─────────── C · The spool: the form steps aside, words run down the page ─────────── */
const Spool = ({ notes, pins, pin }) => {
  const box = useRef(null);
  useEffect(() => { box.current?.scrollTo({ top: box.current.scrollHeight, behavior: "smooth" }); }, [notes.length]);

  return (
    <Chrome label="C · The spool — a margin running down the page">
      <TopBar />
      <div className="flex items-center gap-3 px-5 pb-1 pt-2">
        <div className="shrink-0 overflow-hidden" style={{ width: 86, height: 54 }}>
          <Presence state="speaking" accent={ACCENT} height={54} />
        </div>
        <p className="font-mono text-[8px] uppercase leading-[1.6] tracking-[0.18em]" style={{ color: "var(--text-faint)" }}>
          speaking<br />tap the form to cut in
        </p>
      </div>
      <div ref={box} className="min-h-0 flex-1 overflow-y-auto px-5 pb-3" style={{ scrollbarWidth: "none" }}>
        <div className="relative pl-4">
          <span className="absolute bottom-2 left-0 top-2 w-px" style={{ background: `linear-gradient(180deg, transparent, ${ACCENT}66, transparent)` }} />
          {notes.map((n, i) => (
            <button key={i} onClick={() => pin(i)} className="block w-full py-2.5 text-left">
              <span
                className="relative block rounded-[14px] px-3.5 py-3"
                style={{
                  background: pins.has(i) ? `color-mix(in srgb, ${ACCENT} 10%, rgba(18,17,16,0.6))` : "rgba(244,239,230,0.025)",
                  border: `1px solid ${pins.has(i) ? `color-mix(in srgb, ${ACCENT} 45%, transparent)` : "rgba(244,239,230,0.055)"}`,
                }}
              >
                <span
                  className="absolute -left-[22px] top-5 block h-1.5 w-1.5 rounded-full"
                  style={{ background: pins.has(i) ? ACCENT : "rgba(244,239,230,0.25)", boxShadow: pins.has(i) ? `0 0 12px ${ACCENT}` : "none" }}
                />
                <span className="flex gap-3">
                  <Ordinal i={i} />
                  <Body n={n} small />
                </span>
                {pins.has(i) && (
                  <span className="mt-2 inline-flex items-center gap-1 font-mono text-[7.5px] uppercase tracking-[0.2em]" style={{ color: ACCENT }}>
                    <Pin size={8} /> kept
                  </span>
                )}
              </span>
            </button>
          ))}
        </div>
      </div>
      <Controls />
    </Chrome>
  );
};

export default function MockupNotes() {
  const [count, setCount] = useState(4);
  const [pins, setPins] = useState(new Set());
  const notes = SAMPLE.slice(0, count);
  const pin = (i) =>
    setPins((p) => {
      const n = new Set(p);
      n.has(i) ? n.delete(i) : n.add(i);
      return n;
    });

  return (
    <div className="grain min-h-screen bg-[#121417] text-[#F3EFE6]" data-testid="mockup-notes">
      <div className="relative z-10 mx-auto max-w-[1300px] px-6 py-12">
        <p className="eyebrow">Griot · mobile study</p>
        <h1 className="mt-2 font-display text-3xl font-semibold leading-tight sm:text-4xl">Three ways the words could live on a phone.</h1>
        <p className="mt-3 max-w-2xl text-sm text-[#A1A8B3]">
          All three hold everything from the sitting, scroll on their own, and let you tap a note to keep it —
          a kept note stops fading and gets the ember ring. Add notes to see how each one behaves as they pile up.
        </p>

        <div className="mt-6 flex flex-wrap items-center gap-3">
          <button
            data-testid="mock-add-note"
            onClick={() => setCount((c) => Math.min(SAMPLE.length, c + 1))}
            className="rounded-full px-4 py-2 font-mono text-[10px] uppercase tracking-[0.2em]"
            style={{ background: ACCENT, color: "#0C0C0B" }}
          >
            + another note
          </button>
          <button
            data-testid="mock-reset"
            onClick={() => { setCount(1); setPins(new Set()); }}
            className="rounded-full border border-white/10 px-4 py-2 font-mono text-[10px] uppercase tracking-[0.2em] text-[#A1A8B3]"
          >
            start over
          </button>
          <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#6E7582]">{notes.length} on the table · {pins.size} kept</span>
        </div>

        <div className="mt-10 flex flex-wrap gap-10">
          <Ledger notes={notes} pins={pins} pin={pin} />
          <Stack notes={notes} pins={pins} pin={pin} />
          <Spool notes={notes} pins={pins} pin={pin} />
        </div>
      </div>
    </div>
  );
}
